/* The Ikonis and its Motherboard module in the builder and the sheet view: design the weapon, build and
   install augments, and keep the scrap collection and Quantum. */

import { Minus, Plus, X } from 'lucide-react';
import { IKONIS_DAMAGE_TYPES, IKONIS_RANGES, IKONIS_RULES, MOTHERBOARD, SCRAP } from './content/motherboard';
import { augmentList, augmentSlots, build, install, sell, setQuantum, setScrap, unbuild, uninstall } from './motherboard';
import { Character, Ikonis, needs, Trait, TRAITS } from './model';
import { Chips, FeatureCard, Panel, setAt, StepProps, TextField } from './BuilderParts';
import { capitalize } from './SheetParts';

type Props = Pick<StepProps, 'c' | 'set'>;

const RANGE_CHOICES = IKONIS_RANGES.map((r) => `${r.range} - ${r.damage}`);

/** The weapon's design, from the module's top section. */
export function IkonisPanel({ c, set }: Props) {
  const ik = c.ikonis;
  const edit = (patch: Partial<Ikonis>) => set({ ikonis: { ...ik, ...patch } });
  return (
    <Panel title="Ikonis" needed={needs(c, 'Ikonis')}>
      <p className="cb-hint">{IKONIS_RULES.weapon}</p>
      <div className="cb-ikonis__names">
        <TextField label="Name" value={ik.name} maxLength={30} onChange={(name) => edit({ name })} />
        <TextField label="Description" value={ik.description} maxLength={60} onChange={(description) => edit({ description })} />
      </div>
      <p className="cb-field__label">{MOTHERBOARD.trait}</p>
      <Chips label="Ikonis trait" options={TRAITS.map(capitalize)} isOn={(t) => t.toLowerCase() === ik.trait} onPick={(t) => edit({ trait: t.toLowerCase() as Trait })} />
      <p className="cb-field__label">{MOTHERBOARD.range}</p>
      <Chips label="Ikonis range and damage" options={RANGE_CHOICES} isOn={(o) => o.startsWith(`${ik.range} - `)} onPick={(o) => edit({ range: o.split(' - ')[0] })} />
      <p className="cb-field__label">{MOTHERBOARD.damageType}</p>
      <Chips label="Ikonis damage type" options={IKONIS_DAMAGE_TYPES} isOn={(t) => t === ik.damageType} onPick={(damageType) => edit({ damageType })} />
      <FeatureCard name={MOTHERBOARD.bonded.name}>{MOTHERBOARD.bonded.text}</FeatureCard>
    </Panel>
  );
}

function AugmentsPanel({ c, set }: Props) {
  const slots = augmentSlots(c);
  const change = (next: Character) => set({ ikonis: next.ikonis });
  const full = c.ikonis.installed.length >= slots;
  return (
    <Panel title="Augments">
      <p className="cb-hint">{MOTHERBOARD.slots} {IKONIS_RULES.augments}</p>
      <p className="cb-aug__slots">Installed: {c.ikonis.installed.length} of {slots} slots</p>
      <ul className="cb-aug">
        {augmentList(c).map((a) => (
          <li key={a.name} className={`cb-aug__row${a.installed ? ' is-installed' : ''}`}>
            <span className="cb-aug__boxes" role="img" aria-label={`${a.name}: ${a.built} of ${a.boxes} built`}>
              {Array.from({ length: a.boxes }, (_, i) => <span key={i} className={`cb-aug__box${i < a.built ? ' is-built' : ''}`} />)}
            </span>
            <span className="cb-aug__body">
              <span><strong>{a.name}:</strong> {a.text}</span>
              <em className="cb-aug__cost">{a.cost}{a.precompile ? ` · Precompile: Tier ${a.precompile}` : ''}</em>
            </span>
            <span className="cb-aug__actions">
              <button type="button" className="cb-btn" disabled={!a.buildable} onClick={() => change(build(c, a))} aria-label={`Build ${a.name}`}>Build</button>
              {a.built > a.installed && (
                <button type="button" className="cb-btn cb-btn--primary" disabled={full} onClick={() => change(install(c, a.name))} aria-label={`Install ${a.name}`}>Install</button>
              )}
              {a.installed > 0 && (
                <button type="button" className="cb-btn" onClick={() => change(uninstall(c, a.name))} aria-label={`Remove ${a.name}`}>Remove</button>
              )}
              {a.built > 0 && (
                <button type="button" className="cb-aug__undo" onClick={() => change(unbuild(c, a))} aria-label={`Take ${a.name} apart`} title="Take apart and get the scrap back">
                  <X size={14} aria-hidden="true" />
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Count({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <span className="cb-count" role="group" aria-label={label}>
      <button type="button" aria-label={`Fewer ${label}`} disabled={!value} onClick={() => onChange(value - 1)}><Minus size={14} aria-hidden="true" /></button>
      <input type="number" min={0} aria-label={label} value={value} onChange={(e) => onChange(Math.max(0, Math.floor(Number(e.target.value) || 0)))} />
      <button type="button" aria-label={`More ${label}`} onClick={() => onChange(value + 1)}><Plus size={14} aria-hidden="true" /></button>
    </span>
  );
}

function ScrapPanel({ c, set }: Props) {
  const change = (next: Character) => set({ ikonis: next.ikonis });
  const relics = c.ikonis.relics;
  const setRelics = (list: string[]) => set({ ikonis: { ...c.ikonis, relics: list } });
  return (
    <Panel title="Scrap collection">
      <div className="cb-quantum">
        <span className="cb-quantum__label">Current Quantum</span>
        <Count label="Quantum" value={c.ikonis.quantum} onChange={(n) => change(setQuantum(c, n))} />
      </div>
      {SCRAP.map((g) => (
        <section key={g.group} className="cb-scrap" aria-label={g.group}>
          <h4 className="cb-scrap__title">{g.group} <span>({g.die})</span></h4>
          <ul className="cb-scrap__list">
            {g.items.map((s) => {
              const n = c.ikonis.scrap[s.name] ?? 0;
              return (
                <li key={s.name} className="cb-scrap__item">
                  <span className="cb-scrap__name"><span className="cb-scrap__value">{s.value}</span>{s.name}</span>
                  <Count label={s.name} value={n} onChange={(v) => change(setScrap(c, s.name, v))} />
                  <button type="button" className="cb-btn cb-scrap__sell" disabled={!n} onClick={() => change(sell(c, s.name))} aria-label={`Sell a ${s.name}`}>
                    Sell +{s.value}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      <section className="cb-scrap" aria-label="Relics">
        <h4 className="cb-scrap__title">Relics</h4>
        {relics.map((r, i) => (
          <div key={i} className="cb-relic">
            <TextField label={`Relic ${i + 1}`} value={r} maxLength={30} onChange={(v) => setRelics(setAt(relics, i, v))} />
            <button type="button" className="cb-aug__undo" aria-label={`Remove relic ${i + 1}`} onClick={() => setRelics(relics.filter((_, k) => k !== i))}>
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        ))}
        {relics.length < 6 && (
          <button type="button" className="cb-btn" onClick={() => setRelics([...relics, ''])}>
            <Plus size={16} aria-hidden="true" />Add a relic
          </button>
        )}
      </section>
    </Panel>
  );
}

/** Everything on the module: the weapon, its augments and the scrap collection (the sheet view's Motherboard dialog). */
export function MotherboardPanels(props: Props) {
  return (
    <>
      <IkonisPanel {...props} />
      <AugmentsPanel {...props} />
      <ScrapPanel {...props} />
    </>
  );
}
